import { v4 as uuid } from "uuid";

import { _ } from "js/vendor";
import log from "js/core/logger";
import { PusherEventType, SLIDE_COMMAND_BLACKLIST } from "common/constants";
import Api from "js/core/api";
import pusher from "js/core/services/pusher";
import applyCommands from "common/commands/applyCommands";
import deriveSimpleModel from "common/commands/deriveSimpleModel";
import removeTrailingNullsFromArrays from "common/utils/removeTrailingNullsFromArrays";

import CommandsAdapter from "./commandsAdapter";
import Adapter from "./adapter";

class SlidesAdapter extends Adapter {
    constructor({
        model,
        autoSync = true,
        readonly = false,
        // Only used for passing to commands adapters
        userId = null
    }) {
        super();

        this.model = model;
        this.autoSync = autoSync;
        this.readonly = readonly;
        this.userId = userId;

        this.fallbackToCommandsAdapter = false;
        this.commandsAdapter = null;

        this.connected = false;

        this._asyncActionsPromiseChain = Promise.resolve();

        this.lastModifiedAt = 0;

        this.pusherEventChunksBuffer = {};
    }

    connect({ id = null, onRemoteChange = () => { }, onRemoteError = () => { }, data = null }) {
        log.info(log.ADAPTER)(`[${id}] connect() autoSync: ${this.autoSync} readonly: ${this.readonly}`);

        if (data && id) {
            throw new Error("Initializing slide with both id and initial data is not permitted");
        }

        if (!data && !id) {
            throw new Error("Initial data or id must be present");
        }

        if (this.readonly && data) {
            throw new Error("Cannot create new record in readonly mode");
        }

        // Auto generating an id if not passed
        this.id = _.defaultTo(id, uuid());

        // Wrapping callbacks to allow logging
        this.onRemoteChange = (changeType, data) => {
            // We have to remove trailing nulls from arrays since there's no handy way to entirely remove elements from arrays
            // in Mongo so "removed" elements are marked as nulls instead, so we have to trim them
            const normalizedData = removeTrailingNullsFromArrays(_.cloneDeep(data));
            log.info(log.ADAPTER)(`[${this.id}] onRemoteChange() changeType: ${changeType}\ndata:\n${JSON.stringify(data, null, 2)}normalizedData:\n${JSON.stringify(normalizedData, null, 2)}`);
            return onRemoteChange(changeType, normalizedData);
        };
        this.onRemoteError = err => {
            log.info(log.ADAPTER)(`[${this.id}] onRemoteError() err: ${err}`);
            return onRemoteError(err);
        };

        // Initializing (loading/setting the data)
        this._initialize(data);

        this.connected = true;

        // Returning the model id
        return this.id;
    }

    disconnect() {
        log.info(log.ADAPTER)(`[${this.id}] disconnect()`);

        if (!this.connected) {
            log.info(log.ADAPTER)(`[${this.id}] disconnect() already disconnected, ignoring...`);
            return;
        }

        if (this.pusherChannel) {
            this.pusherChannel.unbind(PusherEventType.DATA_RECORD_UPDATED, this._onPusherEvent);
            // Will unsubscribe if only nobody else is using the channel (e.g. another slide model instance)
            if (Object.keys(this.pusherChannel.callbacks._callbacks).length === 0) {
                this.pusherChannel.unsubscribe();
            }
            this.pusherChannel = null;
        }

        if (this.fallbackToCommandsAdapter) {
            this.commandsAdapter.disconnect();
        }

        this.connected = false;

        // NOTE: there may be still background updates running after the
        // adapter has been disconnected
    }

    update(type, changesetUpdates, changesetOriginal) {
        if (this.fallbackToCommandsAdapter) {
            return this.commandsAdapter.update(type, changesetUpdates, changesetOriginal);
        }

        if (!this.isConnected()) {
            throw new Error("Adapter is not connected");
        }

        if (this.readonly) {
            throw new Error("Adapter is in read only mode, cannot update");
        }

        switch (type) {
            case Adapter.TYPE.remove:
                throw new Error("Remove update type not supported");
            case Adapter.TYPE.replace:
                throw new Error("Replace update type not supported");
            case Adapter.TYPE.update:
                return this._handleUpdate(changesetUpdates, changesetOriginal);
            default:
                throw new Error(`Unsupported update type ${type}`);
        }
    }

    isConnected() {
        if (this.fallbackToCommandsAdapter) {
            return this.commandsAdapter.isConnected();
        }

        return this.connected;
    }

    createUid() {
        if (this.fallbackToCommandsAdapter) {
            return this.commandsAdapter.createUid();
        }

        return uuid();
    }

    _onPusherEvent = payload => {
        this._asyncActionsPromiseChain = this._asyncActionsPromiseChain
            .then(() => {
                // May have been disconnected while was waiting for the changes
                // to be written
                if (!this.connected) {
                    return;
                }

                const decodeAndParseChunksData = encodedData =>
                    // Note: decodeURIComponent and escape are needed in order to correctly handle extended unicode chars (2+ bytes)
                    JSON.parse(decodeURIComponent(escape(atob(encodedData))));

                const handleEvent = (data, triggeredAt) => {
                    if (triggeredAt <= this.lastModifiedAt) {
                        return;
                    }

                    // Data is a changeset
                    this.onRemoteChange(Adapter.TYPE.update, data);
                };

                const {
                    eventId,
                    chunksTotalCount,
                    chunkIndex,
                    chunk
                } = payload;

                // Message has only one chunk -> process immediately
                if (chunksTotalCount === 1) {
                    const { data, triggeredAt } = decodeAndParseChunksData(chunk);
                    handleEvent(data, triggeredAt);
                    return;
                }

                // Creating chunks buffer for event if not exists
                if (!this.pusherEventChunksBuffer[eventId]) {
                    this.pusherEventChunksBuffer[eventId] = new Array(chunksTotalCount);
                }

                // Saving received chunk under its index
                this.pusherEventChunksBuffer[eventId][chunkIndex] = chunk;

                // Checking if we have all chunks and composing the event if we do
                if (this.pusherEventChunksBuffer[eventId].filter(chunk => chunk).length === chunksTotalCount) {
                    const { data, triggeredAt } = decodeAndParseChunksData(this.pusherEventChunksBuffer[eventId].join(""));
                    delete this.pusherEventChunksBuffer[eventId];
                    handleEvent(data, triggeredAt);
                    return;
                }
            })
            .catch(err => {
                // Won't throw here to avoid breaking the promise chain
                log.error(`[adapter] [${this.id}] _onPusherEvent() failed`);
                log.error(err);

                this.onRemoteError(err);
            });
    }

    _handleUpdate(changesetUpdates, changesetOriginal) {
        log.info(log.ADAPTER)(`[${this.id}] _handleUpdate() changesetUpdates:\n${JSON.stringify(changesetUpdates, null, 2)}\changesetOriginal:\n${JSON.stringify(changesetOriginal, null, 2)}`);

        return new Promise((resolve, reject) => {
            this._asyncActionsPromiseChain = this._asyncActionsPromiseChain
                .then(() => Api.slides.put({ id: this.id, changesetUpdates, changesetOriginal }))
                .then(({ modifiedAt }) => this.lastModifiedAt = modifiedAt)
                .then(() => resolve())
                // NOTE: we're chaining catch so the promise chain doesn't get broken
                // but we do call reject to let the caller know we failed
                .catch(err => {
                    log.error(`[adapter] [${this.id}] _handleUpdate() failed`);
                    log.error(err);

                    reject(err);
                });
        });
    }

    _initialize(initialData) {
        log.info(log.ADAPTER)(`[${this.id}] _initialize() initialData:\n${JSON.stringify(initialData, null, 2)}`);

        this._asyncActionsPromiseChain = this._asyncActionsPromiseChain
            .then(async () => {
                log.info(log.ADAPTER)(`[${this.id}] _initialize() started`);

                if (initialData) {
                    // Creating new slide
                    let slideModel;
                    if (initialData.cmds) {
                        log.warn(`[adapter] [${this.id}] _initialize() received initial data with commands, this should never happen, will migrate the model`);
                        slideModel = this._composeSlideModel(initialData);
                    } else {
                        slideModel = { ...initialData, id: this.id };
                    }

                    // Force non-empty states
                    // It can be nulled by StorageModel on update because mergeChanges()
                    // removes empty objects and arrays to mimic Firebase behaviour
                    if (!slideModel.states) {
                        slideModel.states = [{}];
                    }

                    // Saving a new slide with the supplied data
                    slideModel = await Api.slides.post(slideModel);
                    // Set correct modified at value
                    this.lastModifiedAt = slideModel.modifiedAt;
                    // Reporting initialized
                    this.onRemoteChange(Adapter.TYPE.initialize, slideModel);
                    // Connecting pusher (if needed)
                    await this._connectPusher();
                    return;
                }

                // Getting the slide (will migrate it if needed/allowed)
                // The retry logic is necessary to handle cases when we're requesting a slide that's
                // being written to Mongo at the moment so the server may not know about its existence yet
                let slidesApiResponse = null;
                let apiFetchAttemptsLeft = 5;
                while (true) {
                    try {
                        slidesApiResponse = await Api.slides.get({
                            id: this.id,
                            isReadOnlyMode: this.readonly,
                            presentationId: (this.model.presentation && !this.model.presentation.get("isDummy")) ? this.model.presentation.id : null,
                            presentationLinkId: this.model.presentation && this.model.presentation.get("link")?.id,
                        });
                        break;
                    } catch (err) {
                        // 404 means slide not found so it's possible it's being written to Mongo at the moment
                        if (err.status === 404) {
                            if (apiFetchAttemptsLeft > 0) {
                                // Wait for 1s and retry
                                await new Promise(resolve => setTimeout(resolve, 1000));
                                apiFetchAttemptsLeft--;
                                continue;
                            }

                            throw new Error(`Failed to load slide ${this.id}, slide not found`);
                        }

                        // Migration failed, will try falling back to commands adapter
                        log.error(`[adapter] [${this.id}] _initialize() Api.slides.get() request failed, falling back to commands adapter`);
                        log.error(err);
                        this._switchToCommandsAdapter();
                        return;
                    }
                }

                if (slidesApiResponse.migrated) {
                    // Set correct modified at value
                    this.lastModifiedAt = slidesApiResponse.slide.modifiedAt ?? slidesApiResponse.slide.createdAt ?? this.lastModifiedAt;
                    // Reporting initialized
                    this.onRemoteChange(Adapter.TYPE.initialize, slidesApiResponse.slide);
                    // Connecting pusher (if needed)
                    await this._connectPusher();
                    return;
                }

                // Slide wasn't migrated, will use commands adapter
                this._switchToCommandsAdapter();
            })
            .then(() => log.info(log.ADAPTER)(`[${this.id}] _initialize() finished`))
            .catch(err => {
                log.error(`[adapter] [${this.id}] _initialize() failed`);
                log.error(err);

                // NOTE: Since initialize() is called from a sync function
                // we should report errors by calling onRemoteError()
                this.onRemoteError(err);
            });
    }

    async _connectPusher() {
        if (!this.connected) {
            // NOTE: this may happen if disconnect() was called before initialization has finished
            log.info(log.ADAPTER)(`[${this.id}] _connectPusher() adapter is disconnected, ignoring...`);
            return;
        }

        if (!this.autoSync) {
            log.info(log.ADAPTER)(`[${this.id}] _connectPusher() auto sync is disabled, ignoring...`);
            return;
        }

        const pusherChannelId = `private-slide-${this.id}`;

        log.info(log.ADAPTER)(`[${this.id}] _connectPusher() will subscribe to channel ${pusherChannelId}`);

        const pusherChannel = await pusher.subscribe(pusherChannelId);
        if (pusherChannel) {
            if (!this.connected) {
                log.info(log.ADAPTER)(`[${this.id}] _connectPusher() adapter got disconnected while pusher connection was being established, won't subscribe to channel...`);
                // Will unsubscribe if only nobody else is using the channel (e.g. another slide model instance)
                if (Object.keys(pusherChannel.callbacks._callbacks).length === 0) {
                    pusherChannel.unsubscribe();
                }
                return;
            }

            this.pusherChannel = pusherChannel;
            this.pusherChannel.bind(PusherEventType.DATA_RECORD_UPDATED, this._onPusherEvent);

            log.info(log.ADAPTER)(`[${this.id}] _connectPusher() subscribed to channel ${pusherChannelId}`);
        } else {
            log.info(log.ADAPTER)(`[${this.id}] _connectPusher() pusher disabled, ignoring...`);
        }
    }

    _composeSlideModel(firebaseSlide) {
        const commands = Object.entries(firebaseSlide.cmds)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(cmd => {
                cmd[1].id = cmd[0];
                return cmd[1];
            })
            .filter(command => command.id > firebaseSlide.snap.id);

        const updates = commands.reduce((list, cmd) => list.concat(JSON.parse(cmd.updates)), []);
        const commandsModel = applyCommands(JSON.parse(firebaseSlide.snap.model || "{}"), updates);

        // Merging commands fields and plain fields
        const slideModel = { ...firebaseSlide, ...deriveSimpleModel(commandsModel), id: this.id };
        delete slideModel.cmds;
        delete slideModel.snap;

        return slideModel;
    }

    _switchToCommandsAdapter() {
        log.info(log.ADAPTER)(`[${this.id}] _switchToCommandsAdapter() switching to commands adapter...`);

        this.fallbackToCommandsAdapter = true;
        this.commandsAdapter = new CommandsAdapter({
            writablePlainProperties: ["presentationId"],
            plainProperties: SLIDE_COMMAND_BLACKLIST,
            autoSync: this.autoSync,
            readonly: this.readonly,
            userId: this.userId
        });

        // This will trigger the "initialize" event upon connection
        this.commandsAdapter.connect({
            root: "slides",
            id: this.id,
            onRemoteChange: (changeType, data) => {
                // If the slide was migrated, then switch to Mongo
                if ([Adapter.TYPE.replace, Adapter.TYPE.update, Adapter.TYPE.initialize].includes(changeType) && data.migrationFinishedAt) {
                    log.info(log.ADAPTER)(`[${this.id}] onRemoteChange() slide was migrated, switching to Mongo`);
                    this.commandsAdapter.disconnect();
                    this.commandsAdapter = null;
                    this.fallbackToCommandsAdapter = false;
                    // Reinitializing
                    this._initialize(null);
                    return;
                }

                return this.onRemoteChange(changeType, data);
            },
            onRemoteError: this.onRemoteError
        });
    }
}

export default SlidesAdapter;
